import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Icon } from '../components/Icon';
import { type Quote, customerApi, quoteApi } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

export const Route = createFileRoute('/quotes/new')({
  component: NewQuotePage,
});

const INCOTERMS = ['EXW', 'FOB', 'CFR', 'CIF', 'DAP', 'DDP'] as const;
const CURRENCIES = ['USD', 'JPY', 'EUR', 'CNY', 'HKD'] as const;

function NewQuotePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentCompanyId } = useAuthStore();

  const { data: customersData } = useQuery({
    queryKey: ['customers', currentCompanyId],
    queryFn: () => customerApi.list({ limit: 200 }),
  });

  const [form, setForm] = useState({
    customerId: '',
    exwUnitPriceCents: 420,
    qty: 5000,
    innerPackCostPerUnitCents: 30,
    totalCbm: 2.5,
    cartons: 100,
    originPort: 'Shenzhen',
    destPort: 'Yokohama',
    destCountry: 'JP',
    targetIncoterm: 'CIF' as Quote['incoterm'],
    targetCurrency: 'JPY' as Quote['currency'],
    freightRateUsdCentsPerCbm: 500,
    insuranceRate: 0.005,
    destCustomsDutyRate: 0,
    destVatRate: 0.1,
    destLastMileUsdCents: 50000,
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => quoteApi.create(form),
    onSuccess: (quote) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      navigate({ to: '/quotes/$quoteId', params: { quoteId: quote.id } });
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  // 简易计算预览（实际价以 API 为准）
  const preview = useMemo(() => {
    const exw = form.exwUnitPriceCents * form.qty;
    const innerPack = form.innerPackCostPerUnitCents * form.qty;
    const domestic = Math.round(form.totalCbm * 10000);
    const customs = 20000;
    const fob = exw + innerPack + domestic + customs;
    const fx = form.targetCurrency === 'JPY' ? 150 : form.targetCurrency === 'EUR' ? 0.92 : 1;
    const freight = Math.round(form.totalCbm * form.freightRateUsdCentsPerCbm * fx);
    const cfr = fob + freight;
    const insurance = Math.round((cfr / (1 - form.insuranceRate)) * form.insuranceRate);
    const cif = cfr + insurance;
    return { exw, fob, cfr, cif };
  }, [form]);

  const fmt = (cents: number, cur: string) => {
    const amount = cents / 100;
    if (cur === 'JPY') return `¥${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    return `${cur === 'USD' ? '$' : cur === 'EUR' ? '€' : cur} ${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="max-w-5xl">
      <Link to="/quotes" className="text-sm text-accent inline-block mb-3">
        ← 返回报价列表
      </Link>
      <h1 className="text-2xl font-semibold mb-5">创建报价</h1>

      <div className="grid grid-cols-2 gap-4">
        {/* 左：表单 */}
        <div className="bg-surface border border-line rounded p-5">
          <h2 className="text-sm font-semibold mb-3">输入参数</h2>
          <div className="flex flex-col gap-3 text-xs text-ink-2">
            <label className="flex flex-col gap-1">
              客户 *
              <select
                value={form.customerId}
                onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                required
              >
                <option value="">选择客户</option>
                {customersData?.items.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.country})
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                EXW 单价 (USD cents)
                <input
                  type="number"
                  value={form.exwUnitPriceCents}
                  onChange={(e) => setForm({ ...form, exwUnitPriceCents: +e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
              <label className="flex flex-col gap-1">
                数量 (pcs)
                <input
                  type="number"
                  value={form.qty}
                  onChange={(e) => setForm({ ...form, qty: +e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                内包装/件 (cents)
                <input
                  type="number"
                  value={form.innerPackCostPerUnitCents}
                  onChange={(e) => setForm({ ...form, innerPackCostPerUnitCents: +e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
              <label className="flex flex-col gap-1">
                总体积 (CBM)
                <input
                  type="number"
                  step="0.01"
                  value={form.totalCbm}
                  onChange={(e) => setForm({ ...form, totalCbm: +e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                起运港
                <input
                  type="text"
                  value={form.originPort}
                  onChange={(e) => setForm({ ...form, originPort: e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
              <label className="flex flex-col gap-1">
                目的港
                <input
                  type="text"
                  value={form.destPort}
                  onChange={(e) => setForm({ ...form, destPort: e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                目标 Incoterm
                <select
                  value={form.targetIncoterm}
                  onChange={(e) =>
                    setForm({ ...form, targetIncoterm: e.target.value as Quote['incoterm'] })
                  }
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                >
                  {INCOTERMS.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                客户币种
                <select
                  value={form.targetCurrency}
                  onChange={(e) =>
                    setForm({ ...form, targetCurrency: e.target.value as Quote['currency'] })
                  }
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                海运 USD/CBM
                <input
                  type="number"
                  value={form.freightRateUsdCentsPerCbm}
                  onChange={(e) => setForm({ ...form, freightRateUsdCentsPerCbm: +e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
              <label className="flex flex-col gap-1">
                保险费率
                <input
                  type="number"
                  step="0.001"
                  value={form.insuranceRate}
                  onChange={(e) => setForm({ ...form, insuranceRate: +e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                目的国关税
                <input
                  type="number"
                  step="0.01"
                  value={form.destCustomsDutyRate}
                  onChange={(e) => setForm({ ...form, destCustomsDutyRate: +e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
              <label className="flex flex-col gap-1">
                目的国 VAT
                <input
                  type="number"
                  step="0.01"
                  value={form.destVatRate}
                  onChange={(e) => setForm({ ...form, destVatRate: +e.target.value })}
                  className="px-2 py-1.5 border border-line rounded text-sm text-ink"
                />
              </label>
            </div>
          </div>
          {error && (
            <div className="mt-3 px-3 py-2 bg-bad/10 border border-bad rounded text-xs text-bad">
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={!form.customerId || createMutation.isPending}
            className="w-full mt-4 py-2.5 bg-accent text-white rounded font-medium hover:bg-accent-2 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
          >
            <Icon name="zap" size="sm" strokeWidth={2} />
            {createMutation.isPending ? '创建中...' : '创建报价 + 自动算价'}
          </button>
        </div>

        {/* 右：实时预览 */}
        <div className="bg-surface border border-line rounded p-5">
          <h2 className="text-sm font-semibold mb-3">实时预览（{form.targetIncoterm}）</h2>
          <div className="flex flex-col gap-1 text-sm">
            <div className="flex justify-between">
              <span>EXW {form.originPort}</span>
              <span className="text-ink-2">{fmt(preview.exw, form.targetCurrency)}</span>
            </div>
            <div className="flex justify-between">
              <span>FOB {form.originPort}</span>
              <b>{fmt(preview.fob, form.targetCurrency)}</b>
            </div>
            <div className="flex justify-between">
              <span>CFR {form.destPort}</span>
              <b>{fmt(preview.cfr, form.targetCurrency)}</b>
            </div>
            <div className="flex justify-between">
              <span>CIF {form.destPort}</span>
              <b>{fmt(preview.cif, form.targetCurrency)}</b>
            </div>
            <div className="mt-3 px-3 py-2 bg-accent-soft rounded text-xs text-accent-2 flex items-start gap-1.5">
              <Icon name="lightbulb" size="xs" strokeWidth={2} className="shrink-0 mt-0.5" />
              <span>实际算价（含 DAP/DDP）由后端引擎处理，提交后查看完整结果</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
